import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import * as icons from "lucide-react";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setService(null);
    setNotFound(false);
    api
      .get(`/services/${slug}`)
      .then((r) => setService(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="mds-container py-24 text-center" data-testid="service-notfound">
        <h1 className="font-heading text-3xl mb-3">Service not found</h1>
        <Link to="/directory" className="mds-btn mds-btn-primary mt-4">Back to directory</Link>
      </main>
    );
  }

  if (!service) return <main className="mds-container py-24"><p>Loading…</p></main>;

  const Icon = icons[toPascal(service.icon)] || icons.Sparkles;

  return (
    <main data-testid={`service-detail-${service.slug}`}>
      <section className="mds-container pt-10">
        <Link to="/directory" className="mds-link inline-flex items-center gap-1 mb-8" data-testid="back-to-directory">
          <ArrowLeft size={16} /> Directory
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="flex items-start gap-4 mb-6">
              <span
                className="w-14 h-14 rounded-2xl grid place-items-center flex-shrink-0"
                style={{ background: "rgba(132,169,140,0.18)", color: "#6B8F73" }}
              >
                <Icon size={28} strokeWidth={1.5} />
              </span>
              <div>
                <span className="mds-badge">{service.category.replace("-", " ")}</span>
                <h1 className="font-heading font-semibold text-4xl sm:text-5xl mt-3 tracking-tight leading-tight">
                  {service.name}
                </h1>
                <p className="text-lg text-[#4A5D54] mt-3 leading-relaxed">{service.tagline}</p>
              </div>
            </div>

            <div className="mds-card mt-8">
              <h2 className="font-heading font-semibold text-xl mb-3">What it is</h2>
              <p className="text-[#4A5D54] leading-relaxed">{service.description}</p>
            </div>

            <div className="mds-card mt-6">
              <h2 className="font-heading font-semibold text-xl mb-3">How it works</h2>
              <div className="text-[#4A5D54] leading-relaxed whitespace-pre-line">{service.how_it_works}</div>
            </div>

            {service.pros?.length > 0 && (
              <div className="mds-card mt-6">
                <h2 className="font-heading font-semibold text-xl mb-4">Why I recommend it</h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {service.pros.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#4A5D54]">
                      <Check size={18} className="text-[#84A98C] flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="mds-card">
                <div className="text-xs uppercase tracking-wide text-[#7A8C84]">Matt earns approx</div>
                <div className="font-num font-semibold text-4xl mt-1 text-[#1C2321]">
                  £{service.average_monthly_earning?.toFixed(0) ?? 0}
                  <span className="text-lg text-[#7A8C84] font-normal"> /mo</span>
                </div>
                <a
                  href={service.referral_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mds-btn mds-btn-primary w-full mt-6"
                  data-testid={`referral-link-${service.slug}`}
                >
                  Sign up to {service.name} <ArrowUpRight size={16} />
                </a>
                <p className="text-xs text-[#7A8C84] mt-3 leading-relaxed">
                  This is my personal referral link. If you join through it I may earn a small bonus at no cost to you — thank you!
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <div className="pb-16" />
    </main>
  );
}

function toPascal(s = "") {
  return s.split(/[-_ ]/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join("");
}
